<script lang="ts">
    /**
     * ClickmonAd - 랜딩 푸터 위 클릭몬 배너 블록 (D20, 시행 2026-10-01)
     *
     * 켜짐 판정·PC/모바일 판별·아이프레임 src 는 **서버가 끝내서** window.JadEye.clickmon 으로 준다
     * (App\Support\ClickmonAds). 여기서 src 를 다시 짓지 않는다 — SSR 파셜과 두 벌로 갈라지면
     * 한쪽만 고쳐진다.
     *
     * 📐 랜딩은 섹션이 화면 전체 폭이라 옆 여백이 없다 → 세로 배너(rail)는 쓰지 않고, 나머지 자리를
     *    서버가 준 순서대로 한 줄씩 쌓는다(PC 728 / 300·250 · 모바일 320x100 / 300x250 / 320x50).
     * 🏷 "광고" 라벨은 우리 마크업에 — 방침 hc 10-3 · Pin §7 의 약속이다.
     * ℹ️ 셸(LandingApp)에 한 번 두므로 SPA 내부 이동(pushState)에도 다시 로드되지 않는다.
     */
    interface Unit {
        size: string;
        width: number;
        height: number;
        src: string;
    }

    interface Ads {
        on: boolean;
        device: 'pc' | 'mobile' | null;
        slots: Record<string, Unit[]>;
    }

    // company.ts 의 declare global 과 충돌하지 않게 지역 캐스팅으로 읽는다(lib/pin.ts 와 같은 방식).
    const ads = (window as unknown as { JadEye?: { clickmon?: Ads } }).JadEye?.clickmon;

    const rows: Unit[][] = ads?.on
        ? Object.entries(ads.slots ?? {})
              .filter(([slot]) => slot !== 'rail')
              .map(([, units]) => units)
              .filter((units) => units.length > 0)
        : [];

    // allowtransparency·frameborder·scrolling 은 받은 코드 그대로의 문자열 속성이다.
    // Svelte 타입은 allowtransparency 를 boolean 으로 두므로 문자열 그대로 넘기려고 타입을 넓힌다.
    const legacyAttrs: Record<string, string> = { allowtransparency: 'true', frameborder: '0', scrolling: 'no' };
</script>

{#if rows.length > 0}
    <aside class="clickmon" aria-label="광고 영역 (하단)">
        <p class="clickmon-label">광고</p>
        {#each rows as units, i (i)}
            <div class="clickmon-row">
                {#each units as unit (unit.size)}
                    <iframe
                        src={unit.src}
                        width={unit.width}
                        height={unit.height}
                        title="광고"
                        referrerpolicy="no-referrer-when-downgrade"
                        {...legacyAttrs}
                    ></iframe>
                {/each}
            </div>
        {/each}
    </aside>
{/if}

<style>
    .clickmon {
        max-width: 72rem;
        margin: 0 auto;
        padding: 2rem 1.5rem;
        overflow: hidden;
        border-top: 1px solid #e5e7eb;
    }

    .clickmon-label {
        margin: 0 0 0.375rem;
        text-align: center;
        font-size: 0.75rem;
        font-weight: 500;
        letter-spacing: 0.025em;
        color: #6b7280;
    }

    .clickmon-row {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 1rem;
    }

    .clickmon-row + .clickmon-row {
        margin-top: 1rem;
    }

    /* flex 아이템 기본값(shrink 1)이면 좁은 화면에서 찌그러진다 — 소재는 폭 고정이라 오른쪽이 안 보인다 */
    iframe {
        border: 0;
        display: block;
        flex-shrink: 0;
    }

    /* 360px 폰: 좌우 24px 면 줄 폭 312px < 320 배너. SSR 본문(px-4)과 같은 16px 로 줄인다 */
    @media (max-width: 400px) {
        .clickmon {
            padding: 2rem 1rem;
        }
    }
</style>
